import { Mediator } from '@studio/design-system/input';
import { Ellipses } from '@studio/design-system/label';
import { isErrored } from '@studio/models/device/stream';
import { useDoubleClick } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { twMerge } from 'cn';
import type { Props } from './types.js';
import { Meter } from '#~/audio/components/meter/index.js';
import { Settings } from '#~/common/card/index.js';
import type { Behavior } from '#~/common/card/index.js';

/** Audio `<Card>` component. */
export const Card = forwardRef<Behavior, Props>(
  ({ className, device, handler, status, ...props }, forwardedRef) => {
    const [isEditing, setEditing] = useState(false);

    useImperativeHandle(forwardedRef, () => ({
      setEditing
    }));

    const clicks = useDoubleClick({
      onDoubleClick: (): void => {
        setEditing(true);
      },

      onSingleClick: (): void => {
        // @fixme - currently does nothing.
      }
    });

    return (
      <div className={twMerge(isErrored(status) && 'border-error', 'group', className)} {...props}>
        {isEditing ? (
          <Mediator
            focusOnMount
            onCancel={(): void => {
              setEditing(false);
            }}
            onCommit={(nextValue: string): void => {
              const nextName = toString(nextValue);

              if (nextName && nextName !== device.name) {
                handler.onRename(device.id, nextName);
              }

              setEditing(false);
            }}
            selectOnMount
            size='sm'
            value={device.name}
          />
        ) : (
          <div className='flex items-center justify-between'>
            <div
              className='min-w-0 select-none text-sm text-muted transition-colors group-hover:text-foreground'
              {...clicks}
            >
              <Ellipses title={device.name}>{device.name}</Ellipses>
            </div>

            <Settings
              icon='settings'
              onClick={(): void => {
                handler.onSettings(device.id);
              }}
            />
          </div>
        )}

        <Meter deviceId={device.id} />
      </div>
    );
  }
);
